import React, { useEffect, useMemo, useState } from 'react';
import AdminSidebar from '../components/AdminSidebar';

function formatarData(valor) {
  if (!valor) return '—';

  try {
    return new Intl.DateTimeFormat('pt-BR', {
      dateStyle: 'short',
      timeStyle: 'short'
    }).format(new Date(valor));
  } catch {
    return '—';
  }
}

function labelPerfil(perfil) {
  const labels = {
    admin: 'Administrador',
    coach: 'Coach / Mentor / Psicólogo',
    participante: 'Participante'
  };

  return labels[perfil] || perfil;
}

function labelStatus(status) {
  const labels = {
    active: 'Ativo',
    invited: 'Convidado',
    disabled: 'Desativado'
  };

  return labels[status] || status || '—';
}

function statusStyle(status) {
  const styles = {
    active: {
      background: '#E8F6ED',
      color: '#246B3C'
    },
    invited: {
      background: '#FFF4DF',
      color: '#8A5A00'
    },
    disabled: {
      background: '#FFF1F0',
      color: '#9F1C16'
    }
  };

  return styles[status] || {
    background: '#EDF1F5',
    color: '#46505C'
  };
}

const campoStyle = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '10px 11px',
  borderRadius: 8,
  border: '1px solid #CCD3DA',
  fontSize: '0.95rem',
  background: '#fff'
};

export default function AdminUsuarios() {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [busca, setBusca] = useState('');
  const [filtroPerfil, setFiltroPerfil] = useState('todos');
  const [alterandoId, setAlterandoId] = useState(null);

  const [editando, setEditando] = useState(null);
  const [salvando, setSalvando] = useState(false);

  const [form, setForm] = useState({
    nome: '',
    email: '',
    telefone: '',
    empresa: '',
    cargo: '',
    perfil: ''
  });

  async function carregarUsuarios() {
    try {
      setErro('');

      const response = await fetch('/api/admin/usuarios', {
        method: 'GET',
        credentials: 'include'
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.ok) {
        throw new Error(
          data.error || 'Não foi possível carregar os usuários.'
        );
      }

      setUsuarios(data.usuarios || []);
    } catch (e) {
      setErro(
        e.message || 'Não foi possível carregar os usuários.'
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    carregarUsuarios();
  }, []);

  async function alterarStatus(usuario) {
    const novoStatus =
      usuario.status === 'active' ? 'disabled' : 'active';

    const acao =
      novoStatus === 'disabled' ? 'desativar' : 'reativar';

    if (
      !window.confirm(
        `Tem certeza que deseja ${acao} o usuário ${usuario.nome}?`
      )
    ) {
      return;
    }

    try {
      setErro('');
      setAlterandoId(usuario.id);

      const response = await fetch(
        `/api/admin/usuarios/${usuario.id}/status`,
        {
          method: 'PATCH',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            status: novoStatus
          })
        }
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.ok) {
        throw new Error(
          data.error || 'Não foi possível alterar o usuário.'
        );
      }

      setUsuarios(atuais =>
        atuais.map(item =>
          item.id === usuario.id
            ? { ...item, status: novoStatus }
            : item
        )
      );
    } catch (e) {
      setErro(
        e.message || 'Não foi possível alterar o usuário.'
      );
    } finally {
      setAlterandoId(null);
    }
  }

  function abrirEdicao(usuario) {
    setErro('');

    setEditando(usuario);

    setForm({
      nome: usuario.nome || '',
      email: usuario.email || '',
      telefone: usuario.telefone || '',
      empresa: usuario.empresa || '',
      cargo: usuario.cargo || '',
      perfil: Array.isArray(usuario.perfis) && usuario.perfis.length > 0
        ? usuario.perfis[0]
        : ''
    });
  }

  function fecharEdicao() {
    if (salvando) return;

    setEditando(null);

    setForm({
      nome: '',
      email: '',
      telefone: '',
      empresa: '',
      cargo: '',
      perfil: ''
    });
  }

  function alterarCampo(nome, valor) {
    setForm(atual => ({
      ...atual,
      [nome]: valor
    }));
  }

  async function salvarEdicao(event) {
    event.preventDefault();

    if (!editando) return;

    if (!form.nome.trim() || !form.email.trim()) {
      setErro('Nome e e-mail são obrigatórios.');
      return;
    }

    if (!form.perfil) {
      setErro('Selecione um perfil de acesso.');
      return;
    }

    try {
      setErro('');
      setSalvando(true);

      const response = await fetch(
        `/api/admin/usuarios/${editando.id}`,
        {
          method: 'PATCH',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            nome: form.nome.trim(),
            email: form.email.trim(),
            telefone: form.telefone.trim(),
            empresa: form.empresa.trim(),
            cargo: form.cargo.trim(),
            perfil: form.perfil
          })
        }
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.ok) {
        throw new Error(
          data.error || 'Não foi possível salvar as alterações.'
        );
      }

      setUsuarios(atuais =>
        atuais.map(usuario =>
          usuario.id === editando.id
            ? {
                ...usuario,
                nome: data.usuario.nome,
                email: data.usuario.email,
                telefone: data.usuario.telefone,
                empresa: data.usuario.empresa,
                cargo: data.usuario.cargo,
                perfis: data.usuario.perfil
                  ? [data.usuario.perfil]
                  : []
              }
            : usuario
        )
      );

      fecharEdicao();
    } catch (e) {
      setErro(
        e.message || 'Não foi possível salvar as alterações.'
      );
    } finally {
      setSalvando(false);
    }
  }

  const usuariosFiltrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();

    return usuarios.filter(usuario => {
      const perfis = Array.isArray(usuario.perfis)
        ? usuario.perfis
        : [];

      const batePerfil =
        filtroPerfil === 'todos' ||
        perfis.includes(filtroPerfil);

      const texto = [
        usuario.nome,
        usuario.email,
        usuario.empresa,
        usuario.cargo,
        usuario.telefone,
        ...perfis
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      const bateBusca =
        !termo || texto.includes(termo);

      return batePerfil && bateBusca;
    });
  }, [usuarios, busca, filtroPerfil]);

  return (
    <div>
      <AdminSidebar />

      <main
        style={{
          marginLeft: '220px',
          padding: '2rem',
          minHeight: '100vh',
          background: '#f4f4f4'
        }}
      >
        <h1
          style={{
            margin: 0,
            color: '#242F3C',
            fontSize: '2rem',
            fontWeight: 700
          }}
        >
          Usuários
        </h1>

        <p
          style={{
            marginTop: 8,
            marginBottom: 28,
            color: '#697382'
          }}
        >
          Contas com acesso à plataforma ICV.
        </p>

        {erro && (
          <div
            role="alert"
            style={{
              marginBottom: 20,
              padding: '12px 14px',
              borderRadius: 8,
              background: '#FFF3F2',
              color: '#B42318',
              border: '1px solid #FECDCA',
              fontWeight: 600
            }}
          >
            {erro}
          </div>
        )}

        {editando && (
          <form
            onSubmit={salvarEdicao}
            style={{
              background: '#fff',
              borderRadius: 12,
              padding: '1.5rem',
              marginBottom: 24,
              boxShadow: '0 2px 10px rgba(36,47,60,0.06)'
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 20,
                marginBottom: 20
              }}
            >
              <div>
                <h2
                  style={{
                    margin: 0,
                    color: '#242F3C',
                    fontSize: '1.25rem'
                  }}
                >
                  Editar usuário
                </h2>

                <div
                  style={{
                    color: '#697382',
                    marginTop: 5
                  }}
                >
                  Altere os dados cadastrais e os perfis de acesso.
                </div>
              </div>

              <button
                type="button"
                onClick={fecharEdicao}
                disabled={salvando}
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: '#697382',
                  fontWeight: 700,
                  cursor: 'pointer'
                }}
              >
                Fechar
              </button>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns:
                  'repeat(auto-fit, minmax(240px, 1fr))',
                gap: 16
              }}
            >
              <label>
                <div style={{ marginBottom: 6, fontWeight: 700 }}>
                  Nome
                </div>
                <input
                  type="text"
                  value={form.nome}
                  onChange={e =>
                    alterarCampo('nome', e.target.value)
                  }
                  style={campoStyle}
                  required
                />
              </label>

              <label>
                <div style={{ marginBottom: 6, fontWeight: 700 }}>
                  E-mail
                </div>
                <input
                  type="email"
                  value={form.email}
                  onChange={e =>
                    alterarCampo('email', e.target.value)
                  }
                  style={campoStyle}
                  required
                />
              </label>

              <label>
                <div style={{ marginBottom: 6, fontWeight: 700 }}>
                  Telefone
                </div>
                <input
                  type="text"
                  value={form.telefone}
                  onChange={e =>
                    alterarCampo('telefone', e.target.value)
                  }
                  style={campoStyle}
                />
              </label>

              <label>
                <div style={{ marginBottom: 6, fontWeight: 700 }}>
                  Empresa
                </div>
                <input
                  type="text"
                  value={form.empresa}
                  onChange={e =>
                    alterarCampo('empresa', e.target.value)
                  }
                  style={campoStyle}
                />
              </label>

              <label>
                <div style={{ marginBottom: 6, fontWeight: 700 }}>
                  Cargo
                </div>
                <input
                  type="text"
                  value={form.cargo}
                  onChange={e =>
                    alterarCampo('cargo', e.target.value)
                  }
                  style={campoStyle}
                />
              </label>
            </div>

            <div
              style={{
                marginTop: 22
              }}
            >
              <div
                style={{
                  marginBottom: 10,
                  fontWeight: 700,
                  color: '#242F3C'
                }}
              >
                Perfil de acesso
              </div>

              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 18
                }}
              >
                {[
                  ['admin', 'Administrador'],
                  ['coach', 'Coach / Mentor / Psicólogo'],
                  ['participante', 'Participante']
                ].map(([codigo, nome]) => (
                  <label
                    key={codigo}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 7,
                      cursor: 'pointer'
                    }}
                  >
                    <input
                      type="radio"
                      name="perfil-acesso"
                      checked={form.perfil === codigo}
                      onChange={() =>
                        alterarCampo('perfil', codigo)
                      }
                    />
                    {nome}
                  </label>
                ))}
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                gap: 10,
                marginTop: 24
              }}
            >
              <button
                type="submit"
                disabled={salvando}
                style={{
                  background: '#F36611',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  padding: '11px 20px',
                  fontWeight: 700,
                  cursor: salvando ? 'default' : 'pointer',
                  opacity: salvando ? 0.65 : 1
                }}
              >
                {salvando ? 'Salvando...' : 'Salvar alterações'}
              </button>

              <button
                type="button"
                onClick={fecharEdicao}
                disabled={salvando}
                style={{
                  background: '#fff',
                  color: '#242F3C',
                  border: '1px solid #CCD3DA',
                  borderRadius: 8,
                  padding: '11px 20px',
                  fontWeight: 700,
                  cursor: 'pointer'
                }}
              >
                Cancelar
              </button>
            </div>
          </form>
        )}

        <section
          style={{
            background: '#fff',
            borderRadius: 12,
            padding: '1.2rem',
            marginBottom: 20,
            boxShadow: '0 2px 10px rgba(36,47,60,0.06)'
          }}
        >
          <div
            style={{
              display: 'flex',
              gap: 12,
              flexWrap: 'wrap'
            }}
          >
            <input
              type="text"
              value={busca}
              onChange={e => setBusca(e.target.value)}
              placeholder="Buscar por nome, e-mail, empresa ou cargo"
              style={{
                flex: '1 1 420px',
                padding: '11px 12px',
                borderRadius: 8,
                border: '1px solid #CCD3DA',
                fontSize: '0.95rem'
              }}
            />

            <select
              value={filtroPerfil}
              onChange={e => setFiltroPerfil(e.target.value)}
              style={{
                minWidth: 220,
                padding: '11px 12px',
                borderRadius: 8,
                border: '1px solid #CCD3DA',
                background: '#fff',
                fontSize: '0.95rem'
              }}
            >
              <option value="todos">Todos os perfis</option>
              <option value="admin">Administrador</option>
              <option value="coach">
                Coach / Mentor / Psicólogo
              </option>
              <option value="participante">
                Participante
              </option>
            </select>
          </div>
        </section>

        <div
          style={{
            marginBottom: 12,
            color: '#697382',
            fontSize: '0.92rem'
          }}
        >
          {usuariosFiltrados.length} usuário
          {usuariosFiltrados.length === 1 ? '' : 's'}
        </div>

        <section
          style={{
            background: '#fff',
            borderRadius: 12,
            overflowX: 'auto',
            boxShadow: '0 2px 10px rgba(36,47,60,0.06)'
          }}
        >
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              minWidth: 1180
            }}
          >
            <thead style={{ background: '#f7f7f7' }}>
              <tr>
                <th style={th}>Nome</th>
                <th style={th}>E-mail</th>
                <th style={th}>Perfil</th>
                <th style={th}>Empresa</th>
                <th style={th}>Cargo</th>
                <th style={th}>Status</th>
                <th style={th}>Criado em</th>
                <th style={th}>Último acesso</th>
                <th style={th}>Ações</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan="9"
                    style={{
                      padding: '2rem',
                      textAlign: 'center',
                      color: '#697382'
                    }}
                  >
                    Carregando usuários...
                  </td>
                </tr>
              ) : (
                usuariosFiltrados.map(usuario => (
                  <tr key={usuario.id}>
                    <td style={td}>
                      <strong>{usuario.nome || '—'}</strong>
                    </td>

                    <td style={td}>{usuario.email || '—'}</td>

                    <td style={td}>
                      {(usuario.perfis || []).length > 0
                        ? usuario.perfis
                            .map(labelPerfil)
                            .join(', ')
                        : '—'}
                    </td>

                    <td style={td}>{usuario.empresa || '—'}</td>

                    <td style={td}>{usuario.cargo || '—'}</td>

                    <td style={td}>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '6px 10px',
                          borderRadius: 999,
                          fontWeight: 700,
                          fontSize: '0.86rem',
                          ...statusStyle(usuario.status)
                        }}
                      >
                        {labelStatus(usuario.status)}
                      </span>
                    </td>

                    <td style={td}>
                      {formatarData(usuario.created_at)}
                    </td>

                    <td style={td}>
                      {formatarData(usuario.last_login_at)}
                    </td>

                    <td style={td}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          whiteSpace: 'nowrap'
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => abrirEdicao(usuario)}
                          style={{
                            border: 'none',
                            borderRadius: 7,
                            padding: '7px 12px',
                            fontWeight: 700,
                            cursor: 'pointer',
                            background: '#F36611',
                            color: '#fff'
                          }}
                        >
                          Editar
                        </button>

                        {usuario.perfis?.includes('admin') ? (
                          <span
                            style={{
                              color: '#98A2B3',
                              fontSize: '0.9rem'
                            }}
                          >
                            Protegido
                          </span>
                        ) : (
                          <button
                            type="button"
                            disabled={
                              alterandoId === usuario.id
                            }
                            onClick={() =>
                              alterarStatus(usuario)
                            }
                            style={{
                              border: 'none',
                              borderRadius: 7,
                              padding: '7px 12px',
                              fontWeight: 700,
                              cursor:
                                alterandoId === usuario.id
                                  ? 'default'
                                  : 'pointer',
                              background:
                                usuario.status === 'active'
                                  ? '#232A34'
                                  : '#F36611',
                              color: '#fff',
                              opacity:
                                alterandoId === usuario.id
                                  ? 0.6
                                  : 1
                            }}
                          >
                            {alterandoId === usuario.id
                              ? 'Aguarde...'
                              : usuario.status === 'active'
                                ? 'Desativar'
                                : 'Reativar'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}

              {!loading &&
                usuariosFiltrados.length === 0 && (
                  <tr>
                    <td
                      colSpan="9"
                      style={{
                        padding: '2rem',
                        textAlign: 'center',
                        color: '#697382'
                      }}
                    >
                      Nenhum usuário encontrado.
                    </td>
                  </tr>
                )}
            </tbody>
          </table>
        </section>
      </main>
    </div>
  );
}

const th = {
  padding: 14,
  borderBottom: '2px solid #e6e6e6',
  textAlign: 'left',
  color: '#242F3C',
  whiteSpace: 'nowrap'
};

const td = {
  padding: 14,
  borderBottom: '1px solid #eef0f2',
  verticalAlign: 'middle'
};


